// libraries.
import { Form } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';

// styles.
import { WrapperInputField } from '@shared/InputField/styles';
import { ConfidenceLevel } from '@domains/documents-review/hooks/useFieldConfidence';
import { withRequiredMark } from '@shared/utils/fieldLabel';

interface NumericFieldProps {
  label: string;
  name: string;
  min?: number;
  max?: number;
  placeholder?: string;
  step?: number;
  required?: boolean;
  confidenceLevel?: ConfidenceLevel;
  confidenceBorderColor?: string;
  confidenceBoxShadow?: string;
  onFieldEdit?: (fieldName: string) => void;
}

export const NumericField = ({
  label,
  name,
  min = 0,
  max = 20,
  step = 1,
  placeholder = '',
  required = false,
  confidenceLevel,
  confidenceBorderColor,
  confidenceBoxShadow,
  onFieldEdit,
}: NumericFieldProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const validationRules = required ? { required: 'Campo requerido' } : {};

  const confidenceStyle =
    confidenceLevel && confidenceBorderColor && confidenceBoxShadow
      ? {
          border: `1px solid ${confidenceBorderColor}`,
          boxShadow: confidenceBoxShadow,
        }
      : {};

  const isDisabled = confidenceLevel === 'HIGH';

  // Get register props and extend onChange
  const { onChange: registerOnChange, ...registerProps } = register(
    name,
    validationRules
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Call the onFieldEdit callback when user edits
    if (onFieldEdit) {
      onFieldEdit(name);
    }
    // Call original react-hook-form onChange
    return registerOnChange(e);
  };

  return (
    <WrapperInputField>
      <Form.Group>
        <Form.Label htmlFor={name}>{withRequiredMark(label, required)}</Form.Label>
        <Form.Control
          id={name}
          type="number"
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          style={confidenceStyle}
          disabled={isDisabled}
          onChange={handleChange}
          onKeyDown={e => {
            const value = (e.target as HTMLInputElement).value;
            const isNumber = /^\d$/.test(e.key);
            const isAllowedKey =
              ['Backspace', 'Delete'].includes(e.key) ||
              e.key.includes('Arrow');

            if ((value.length >= 2 || !isNumber) && !isAllowedKey) {
              e.preventDefault();
            }
          }}
          {...registerProps}
        />
      </Form.Group>
      <div className="d-flex justify-content-center">
        {errors[name] && (
          <small className="text-danger my-1 fw-bold">
            {errors[name]?.message as string}
          </small>
        )}
      </div>
    </WrapperInputField>
  );
};
